import { ref, watch, type Ref } from 'vue';

export interface SidebarLayout {
  collapsed: Ref<boolean>;
  width: Ref<number>;
  collapsedRailWidth: Ref<number>;
  setWidth: (pixel: number) => void;
  setCollapsed: (v: boolean) => void;
  toggle: () => void;
  setNavHeight: (pixel: number) => void;
  onPointerDown: (e: PointerEvent) => void;
}

export interface SidebarLayoutOptions {
  navHeight?: number;
  minWidth?: number;
  collapsedRailWidth?: number;
}

const LS_WIDTH = 'itp/sidebar/width';
const LS_COLLAPSED = 'itp/sidebar/collapsed';
const DEFAULT_COLLAPSED_RAIL_WIDTH = 48;
const DEFAULT_MIN_WIDTH = 180;
const DEFAULT_NAV_HEIGHT = 56;
const MAX_WIDTH_RATIO = 0.5;

const collapsed = ref<boolean>(false);
const width = ref<number>(240);
const collapsedRailWidth = ref<number>(DEFAULT_COLLAPSED_RAIL_WIDTH);

let minWidth = DEFAULT_MIN_WIDTH;
let initialized = false;
let initialNavHeightApplied = false;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function getMaxWidth(): number {
  if (!isBrowser()) {
    return Number.MAX_SAFE_INTEGER;
  }
  return Math.floor(window.innerWidth * MAX_WIDTH_RATIO);
}

function clampWidth(pixel: number): number {
  return Math.max(minWidth, Math.min(getMaxWidth(), Math.round(pixel)));
}

function clampWidthInPlace() {
  const maxW = getMaxWidth();
  if (width.value > maxW) {
    width.value = Math.max(minWidth, maxW);
  }
}

function setCssVar(name: string, value: string) {
  if (isBrowser()) {
    document.documentElement.style.setProperty(name, value);
  }
}

function applySidebarW() {
  setCssVar(
    '--itp-sidebar-w',
    `${collapsed.value ? collapsedRailWidth.value : width.value}px`,
  );
}

function hydrateFromStorage() {
  if (!isBrowser()) {
    return;
  }
  try {
    const rawCollapsed = localStorage.getItem(LS_COLLAPSED);
    if (rawCollapsed != null) {
      collapsed.value = JSON.parse(rawCollapsed);
    }
  } catch (e) {
    console.warn('sidebar: failed to read collapsed from storage', e);
  }
  const rawWidth = Number(localStorage.getItem(LS_WIDTH));
  if (Number.isFinite(rawWidth) && rawWidth > 0) {
    width.value = rawWidth;
  }
}

function bindReactivePersistence() {
  watch(
    [collapsed, width, collapsedRailWidth],
    () => {
      if (isBrowser()) {
        try {
          localStorage.setItem(LS_COLLAPSED, JSON.stringify(collapsed.value));
          localStorage.setItem(LS_WIDTH, String(width.value));
        } catch (e) {
          console.warn('sidebar: failed to write to storage', e);
        }
      }
      applySidebarW();
    },
    { immediate: true },
  );
}

function ensureInitialized() {
  if (initialized) {
    return;
  }
  initialized = true;
  hydrateFromStorage();
  clampWidthInPlace();
  setCssVar('--itp-sidebar-transition', '0.15s ease');
  bindReactivePersistence();
  if (isBrowser()) {
    window.addEventListener('resize', clampWidthInPlace);
  }
}

function setWidth(pixel: number) {
  width.value = clampWidth(pixel);
}

function setCollapsed(v: boolean) {
  collapsed.value = v;
}

function toggle() {
  collapsed.value = !collapsed.value;
}

function setNavHeight(pixel: number) {
  setCssVar('--itp-nav-h', `${pixel}px`);
  initialNavHeightApplied = true;
}

function onPointerDown(e: PointerEvent) {
  if (!isBrowser()) {
    return;
  }
  e.preventDefault();
  const startX = e.clientX;
  const startW = width.value;
  const snapThreshold = collapsedRailWidth.value + 30;

  setCssVar('--itp-sidebar-transition', 'none');

  const onMove = (ev: PointerEvent) => {
    const next = startW + (ev.clientX - startX);
    width.value = Math.min(
      getMaxWidth(),
      Math.max(collapsedRailWidth.value, Math.round(next)),
    );
    if (collapsed.value && width.value > snapThreshold) {
      collapsed.value = false;
    }
  };

  const onUp = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp, true);
    if (width.value <= snapThreshold) {
      collapsed.value = true;
    } else {
      width.value = clampWidth(width.value);
    }
    setCssVar('--itp-sidebar-transition', '0.15s ease');
  };

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp, true);
}

function buildApi(): SidebarLayout {
  return {
    collapsed,
    width,
    collapsedRailWidth,
    setWidth,
    setCollapsed,
    toggle,
    setNavHeight,
    onPointerDown,
  };
}

export function provideSidebarLayout(
  opts?: SidebarLayoutOptions,
): SidebarLayout {
  ensureInitialized();

  if (opts?.minWidth != null) {
    minWidth = opts.minWidth;
  }
  if (opts?.collapsedRailWidth != null) {
    collapsedRailWidth.value = opts.collapsedRailWidth;
  }
  if (opts?.navHeight != null || !initialNavHeightApplied) {
    setCssVar('--itp-nav-h', `${opts?.navHeight ?? DEFAULT_NAV_HEIGHT}px`);
    initialNavHeightApplied = true;
  }

  return buildApi();
}

export function useSidebarLayout(): SidebarLayout {
  ensureInitialized();
  return buildApi();
}
